import { Id } from 'types/definitions'
import { computed, makeObservable } from 'mobx'
import DocumentStore, { type UserDocumentsQuery } from 'documents/DocumentStore'
import Document from 'documents/Document'
import { GetCurrentMember } from 'iam/SessionStore'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import { AlertFactory } from 'alerts/Alert'
import { GetCurrentUserWorkspaceMemberships } from 'iam/WorkspaceMembershipStore'
import { DocumentActionsAdapter } from '../DocumentActionsAdapter'

export interface UserDocumentsViewModelPort {
  /**
   * Getter for the current member (selected workspace).
   */
  getCurrentMember: GetCurrentMember

  /**
   * Getter for current user workspaces.
   */
  getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMemberships

  /**
   * Returns the documents that pertain to whichever
   * subject we are working with
   */
  readonly documents: Document[]

  /**
   * Returns a scroll provider for fetching
   * user documents in the specified workspace
   * @param workspaceId
   */
  getWorkspaceDocsScrollProvider: (
    workspaceId: string,
  ) => InfiniteScrollProvider<Document, Record<string, unknown>>

  /**
   * Returns a scroll provider for fetching
   * user documents accross all workspaces
   */
  getUserDocsScrollProvider: () => InfiniteScrollProvider<
    Document,
    Record<string, unknown>
  >

  /**
   * Alert factory
   */
  newAlert: AlertFactory

  /**
   * Adapter for common document actions (download/archive/uploads)
   */
  documentActionsAdapter: DocumentActionsAdapter
}

export class UserDocumentsViewModelAdapter
  implements UserDocumentsViewModelPort
{
  constructor(
    public getCurrentMember: GetCurrentMember,
    public getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private documentStore: DocumentStore,
    public newAlert: AlertFactory,
    public documentActionsAdapter: DocumentActionsAdapter,
  ) {
    makeObservable(this)
  }

  @computed
  get documents() {
    const userDocs = this.documentStore.forUser(this.getCurrentMember().user)
    return userDocs
  }

  getWorkspaceDocsScrollProvider(workspaceId: string) {
    return this.documentStore.getScrollProvider(
      this.getKeyForWorkspaceDocsScrollProvider(workspaceId),
      (params) => {
        return this.documentStore.searchDocuments({
          ...this.getFetchParamsFor(workspaceId),
          ...params,
        })
      },
    )
  }

  getUserDocsScrollProvider() {
    return this.documentStore.getScrollProvider(
      this.getKeyForUserDocsScrollProvider(),
      (params) => {
        return this.documentStore.searchDocuments({
          ...this.getFetchParamsFor(),
          ...params,
        })
      },
    )
  }

  private getKeyForWorkspaceDocsScrollProvider(workspaceId: string) {
    const userId = this.getCurrentMember().user.id
    return `UserDocuments/${workspaceId}/${userId}`
  }

  private getKeyForUserDocsScrollProvider() {
    const userId = this.getCurrentMember().user.id
    return `UserDocuments/${userId}`
  }

  private getFetchParamsFor(workspaceId?: Id): UserDocumentsQuery {
    return {
      context: 'User',
      workspaceId,
    }
  }
}
